import React, { useEffect, useState } from 'react';
import AddEventForm from './AddEventForm';
import EventGroupList from './EventGroupList';
import { fetchApi } from '../services/api';
import { Calendar, Trash2, ExternalLink, Share2, Clock, Pencil, Check, X } from 'lucide-react';

export default function EventList({ channelId, channelName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupRefreshKey, setGroupRefreshKey] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ title: '', description: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const startEditing = (event) => {
    setEditingId(event.id);
    setEditData({ title: event.title || '', description: event.description || '', url: event.url || '' });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({ title: '', description: '', url: '' });
  };

  const saveEditing = async (eventId) => {
    if (!editData.title.trim()) return;
    setSaving(true);
    try {
      const updated = await fetchApi(`/events/${eventId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, ...updated } : e));
      setEditingId(null);
    } catch (err) {
      console.error('Güncelleme hatası:', err);
      alert('Güncelleme sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedGroupId) return;
      setLoading(true);
      try {
        const data = await fetchApi(`/events/group/${selectedGroupId}`);
        setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Etkinlikler alınamadı:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [selectedGroupId, groupRefreshKey]);

  useEffect(() => {
    if (!channelId) return;
    if (selectedGroupId == null) {
      localStorage.removeItem(`pupl_selectedGroup_${channelId}`);
    } else {
      localStorage.setItem(`pupl_selectedGroup_${channelId}`, String(selectedGroupId));
    }
  }, [selectedGroupId, channelId]);

  const handleDelete = async (eventId) => {
    if (!window.confirm("Bu etkinliği silmek istediğinize emin misiniz?")) return;
    try {
      await fetchApi(`/events/${eventId}`, { method: 'DELETE' });
      setEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (err) {
      console.error("Silme hatası:", err);
      alert(err.message || "Silme sırasında hata oluştu.");
    }
  };

  // Timezone dönüşümü olmadan parse et (Z varsa kaldır, boşluğu T yap)
  const parseLocalDate = (str) => new Date(String(str).replace(' ', 'T').replace(/\.000Z$|Z$/, ''));

  const formatEventTime = (start, end) => {
    const startDate = parseLocalDate(start);
    const endDate = parseLocalDate(end);
    const dateStr = startDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    const startTime = startDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return { dateStr, startTime, endTime };
  };

  return (
    <div style={{
      padding: '0.5rem 0',
      maxWidth: '900px',
      margin: '0 auto',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Header */}
      <h1 style={{
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: '700',
        color: 'var(--primary-400)',
        marginBottom: '1.5rem',
        letterSpacing: '-0.02em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}>
        <Calendar size={24} />
        <span>{channelName} Etkinlikleri</span>
      </h1>

      {/* Group List */}
      <EventGroupList
        channelId={channelId}
        selectedGroupId={selectedGroupId}
        onSelectGroup={setSelectedGroupId}
        onGroupRefresh={() => setGroupRefreshKey(k => k + 1)}
      />

      {/* Add Event Form */}
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1.5px solid var(--border-color)',
        borderRadius: 'var(--radius-md)',
        marginBottom: '1.5rem',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
      }}>
        <AddEventForm
          channelId={channelId}
          selectedGroupId={selectedGroupId}
          onSelectGroup={setSelectedGroupId}
          onEventAdded={() => {
            setLoading(true);
            setGroupRefreshKey(k => k + 1);
          }}
        />
      </div>

      {/* Share Link */}
      {selectedGroupId && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}>
          <h2 style={{
            color: 'var(--text-primary)',
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: '600',
          }}>
            Etkinlikler
          </h2>

          <button
            onClick={() => {
              const shareLink = `${window.location.origin}/public/${channelId}/${selectedGroupId}`;
              navigator.clipboard.writeText(shareLink);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            style={{
              background: copied ? 'var(--primary-700)' : 'var(--primary-600)',
              color: '#fff',
              padding: '0.5rem 1rem',
              border: 'none',
              borderRadius: 'var(--radius-full)',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={(e) => {
              if (!copied) e.currentTarget.style.background = 'var(--primary-700)';
            }}
            onMouseLeave={(e) => {
              if (!copied) e.currentTarget.style.background = 'var(--primary-600)';
            }}
          >
            {copied ? <><Check size={15} /> Kopyalandı</> : <><Share2 size={15} /> Linki Kopyala</>}
          </button>
        </div>
      )}

      {/* Events */}
      {loading || events.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: 'var(--text-muted)',
          fontSize: '1rem',
        }}>
          <Calendar size={32} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
          <p>Bu grupta henüz etkinlik yok.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {events.map((event, index) => {
            const { dateStr, startTime, endTime } = formatEventTime(event.start_time, event.end_time);
            return (
              <div
                key={event.id}
                style={{
                  position: 'relative',
                  background: 'var(--bg-elevated)',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.1rem 1.25rem',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'all var(--transition-fast)',
                  animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  e.currentTarget.style.borderColor = 'var(--primary-200)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                {/* Action buttons */}
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  display: 'flex',
                  gap: '4px',
                }}>
                  {editingId === event.id ? (
                    <>
                      <button
                        onClick={() => saveEditing(event.id)}
                        disabled={saving}
                        style={{
                          background: 'var(--accent-50)',
                          color: 'var(--accent-600)',
                          border: '1px solid var(--accent-200)',
                          borderRadius: 'var(--radius-sm)',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: saving ? 'wait' : 'pointer',
                          transition: 'all var(--transition-fast)',
                        }}
                        title="Kaydet"
                      >
                        <Check size={15} />
                      </button>
                      <button
                        onClick={cancelEditing}
                        style={{
                          background: 'var(--neutral-100)',
                          color: 'var(--text-muted)',
                          border: '1px solid var(--border-color)',
                          borderRadius: 'var(--radius-sm)',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                        }}
                        title="İptal"
                      >
                        <X size={15} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEditing(event)}
                        style={{
                          background: 'var(--primary-50)',
                          color: 'var(--primary-500)',
                          border: '1px solid var(--primary-100)',
                          borderRadius: 'var(--radius-sm)',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--primary-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--primary-50)';
                        }}
                        title="Düzenle"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        style={{
                          background: 'var(--danger-50)',
                          color: 'var(--danger-500)',
                          border: '1px solid var(--danger-100)',
                          borderRadius: 'var(--radius-sm)',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--danger-100)';
                          e.currentTarget.style.color = 'var(--danger-600)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--danger-50)';
                          e.currentTarget.style.color = 'var(--danger-500)';
                        }}
                        title="Sil"
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>

                {/* Event Title */}
                {editingId === event.id ? (
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData(d => ({ ...d, title: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && saveEditing(event.id)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.65rem',
                      margin: '0 0 0.4rem 0',
                      border: '1.5px solid var(--primary-300)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: 'var(--primary-400)',
                      background: 'var(--bg-secondary)',
                      outline: 'none',
                      paddingRight: '5rem',
                    }}
                  />
                ) : (
                  <h3 style={{
                    margin: '0 0 0.4rem 0',
                    color: 'var(--primary-400)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    paddingRight: '5rem',
                  }}>
                    {event.title}
                  </h3>
                )}

                {/* Time */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  marginBottom: '0.5rem',
                }}>
                  <Clock size={14} />
                  {dateStr}, {startTime} – {endTime}
                </div>

                {/* Description */}
                {editingId === event.id ? (
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData(d => ({ ...d, description: e.target.value }))}
                    placeholder="Açıklama (isteğe bağlı)"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.65rem',
                      margin: '0 0 0.4rem 0',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '1rem',
                      color: 'var(--text-primary)',
                      background: 'var(--bg-secondary)',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '50px',
                      fontFamily: 'inherit',
                    }}
                  />
                ) : event.description ? (
                  <p style={{
                    margin: '0 0 0.3rem 0',
                    color: 'var(--text-secondary)',
                    fontSize: '1rem',
                    lineHeight: '1.5',
                  }}>
                    {event.description}
                  </p>
                ) : null}

                {/* URL */}
                {editingId === event.id ? (
                  <input
                    type="url"
                    value={editData.url}
                    onChange={(e) => setEditData(d => ({ ...d, url: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && saveEditing(event.id)}
                    placeholder="https://..."
                    style={{
                      width: '100%',
                      padding: '0.45rem 0.65rem',
                      border: '1.5px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      color: 'var(--text-primary)',
                      background: 'var(--bg-secondary)',
                      outline: 'none',
                    }}
                  />
                ) : event.url ? (
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: 'var(--primary-500)',
                      fontSize: '0.875rem',
                      marginTop: '0.25rem',
                      textDecoration: 'none',
                      fontWeight: '500',
                    }}
                  >
                    <ExternalLink size={13} />
                    Bağlantıya Git
                  </a>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
